<template>
  <div class="ai-search-container">
    <div
      class="ai-search-wrap"
      :class="{ 'ai-mode': isAiMode }"
      @mouseenter="handleSearchEnter"
      @mouseleave="handleSearchLeave"
    >
      <button
        class="ai-toggle"
        :class="{ active: isAiMode }"
        @click="handleToggleAi"
        title="切换AI模式"
      >
        <span class="toggle-label">AI</span>
        <span class="toggle-dot"></span>
      </button>
      <input
        type="text"
        class="ai-input"
        v-model="inputValue"
        :placeholder="placeholder"
        @keydown.enter="handleEnter"
        @input="onInput"
        @focus="handleFocus"
        @blur="handleBlur"
      />
      <button class="search-btn" :class="{ loading: isLoading }" @click="handleClick">
        <span class="search-text">{{ buttonText }}</span>
        <span class="loading-spin"></span>
      </button>
    </div>
    <div
      class="preview-panel"
      :class="{ visible: shouldShowPreview }"
      @mouseenter="handlePreviewEnter"
      @mouseleave="handlePreviewLeave"
    >
      <div class="preview-content" :class="{ loading: isLoading }">
        {{ previewContent }}
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'SearchBox',
  props: {
    isAiMode: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: '搜索内容'
    },
    buttonText: {
      type: String,
      default: 'Search'
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    previewContent: {
      type: String,
      default: ''
    },
    isPreviewVisible: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      inputValue: '',
      isSearchHovering: false,
      isPreviewHovering: false,
      isFocused: false
    }
  },
  computed: {
    shouldShowPreview() {
      const hasContent = this.inputValue.trim().length > 0
      const hasResult = this.isLoading || this.previewContent.length > 0
      const isActive =
        this.isFocused || this.isSearchHovering || this.isPreviewHovering || this.isLoading
      return hasContent && hasResult && isActive
    }
  },
  watch: {
    isLoading(newVal, oldVal) {
      // 加载完成后，如果鼠标不在搜索框或预览框内，则关闭预览框
      if (oldVal === true && newVal === false) {
        if (!this.isFocused && !this.isSearchHovering && !this.isPreviewHovering) {
          this.$emit('close-preview')
        }
        // 加载完成后自动聚焦到输入框
        this.$nextTick(() => {
          this.$el.querySelector('.ai-input')?.focus()
        })
      }
    }
  },
  methods: {
    handleToggleAi() {
      this.$emit('toggle-ai')
    },
    onInput() {
      this.$emit('input', this.inputValue)
    },
    handleEnter() {
      this.$emit('search', this.inputValue)
    },
    handleClick() {
      this.$emit('search', this.inputValue)
    },
    handleFocus() {
      this.isFocused = true
    },
    handleBlur() {
      this.isFocused = false
    },
    handleSearchEnter() {
      this.isSearchHovering = true
    },
    handleSearchLeave() {
      this.isSearchHovering = false
      if (!this.isFocused && !this.isPreviewHovering) {
        this.$emit('close-preview')
      }
    },
    handlePreviewEnter() {
      this.isPreviewHovering = true
    },
    handlePreviewLeave() {
      this.isPreviewHovering = false
      if (!this.isFocused) {
        this.$emit('close-preview')
      }
    }
  }
}
</script>

<style scoped>
.ai-search-container {
  position: relative;
  width: 100%;
  max-width: 420px;
  min-width: 260px;
}

.ai-search-wrap {
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 999px;
  padding: 5px;
  gap: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  transition: all 0.24s ease;
  overflow: hidden;
}

.ai-search-wrap:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(136, 116, 255, 0.4);
}

.ai-search-wrap:focus-within {
  box-shadow: 0 0 0 2px rgba(136, 116, 255, 0.3), 0 0 12px rgba(136, 116, 255, 0.1);
  border-color: rgba(136, 116, 255, 0.5);
}

.ai-search-wrap.ai-mode {
  background: rgba(136, 116, 255, 0.12);
  border-color: rgba(136, 116, 255, 0.35);
}

.ai-search-wrap.ai-mode:focus-within {
  box-shadow: 0 0 0 2px rgba(136, 116, 255, 0.4), 0 0 15px rgba(136, 116, 255, 0.15);
}

.ai-toggle {
  flex-shrink: 0;
  width: 50px;
  height: 24px;
  border-radius: 12px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  cursor: pointer;
  position: relative;
  display: flex;
  align-items: center;
  padding: 0;
  transition: all 0.3s ease;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}

.ai-toggle:hover {
  background: rgba(255, 255, 255, 0.15);
}

.ai-toggle.active {
  background: linear-gradient(135deg, #147381, #8874ff);
}

.toggle-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ccc6ef;
  position: absolute;
  left: 2px;
  top: 2px;
  transition: transform 0.3s ease;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
}

.ai-toggle.active .toggle-dot {
  transform: translateX(26px);
}

.toggle-label {
  position: absolute;
  right: 12px;
  top: 53%;
  line-height: 100%;
  transform: translateY(-50%);
  font-size: 11px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.3s ease;
  z-index: 1;
  pointer-events: none;
}

.ai-toggle.active .toggle-label {
  color: #ffffff;
  right: 28px;
}

.ai-input {
  flex: 1;
  min-width: 0;
  height: 32px;
  background: transparent;
  border: none;
  outline: none;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
  padding: 0 4px;
}

.ai-input::placeholder {
  color: rgba(255, 255, 255, 0.45);
}

.search-btn {
  width: 60px;
  flex-shrink: 0;
  height: 32px;
  padding: 0 14px;
  border-radius: 999px;
  border: none;
  background: #7058e0;
  color: white;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
}

.search-btn:hover {
  background: #8874ff;
}

.ai-mode .search-btn {
  background: linear-gradient(135deg, #147381, #8874ff);
}

.ai-mode .search-btn:hover {
  opacity: 0.9;
}

.search-btn.loading {
  background: #5a45c7 !important;
  pointer-events: none;
}

.ai-mode .search-btn.loading {
  background: linear-gradient(1deg, #3db89d, #6a54cc) !important;
}

.loading-spin {
  display: none;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.search-btn.loading .search-text {
  display: none;
}

.search-btn.loading .loading-spin {
  display: block;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.preview-panel {
  position: absolute;
  top: calc(100% + 8px);
  left: -6px;
  right: -6px;
  background: rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(12px);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 12px 16px;
  opacity: 0;
  visibility: hidden;
  transform: translateY(-8px);
  transition: all 0.2s ease;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  z-index: 100;
  max-height: 160px;
  overflow-y: auto;
}

/* 自定义滚动条样式 */
.preview-panel::-webkit-scrollbar {
  width: 4px;
}

.preview-panel::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 2px;
}

.preview-panel::-webkit-scrollbar-thumb {
  background: rgba(105, 176, 255, 0.4);
  border-radius: 2px;
}

.preview-panel::-webkit-scrollbar-thumb:hover {
  background: rgba(105, 176, 255, 0.6);
}

.preview-panel.visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.preview-content {
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  line-height: 1.6;
  min-height: 20px;
  white-space: pre-wrap;
  word-break: break-word;
}

.preview-content.loading {
  color: rgba(255, 255, 255, 0.5);
}

.preview-content.loading::after {
  content: '.';
  animation: dots 1.5s infinite;
}

@keyframes dots {
  0%,
  20% {
    content: '.';
  }
  40% {
    content: '..';
  }
  60% {
    content: '...';
  }
  80%,
  100% {
    content: '....';
  }
}

@media (max-width: 1024px) {
  .ai-search-container {
    max-width: 360px;
  }
}

@media (max-width: 768px) {
  .ai-search-container {
    max-width: 100%;
    min-width: 0;
    width: auto;
    flex: 1;
  }

  .ai-search-wrap {
    padding: 4px;
    gap: 6px;
  }

  .ai-toggle {
    width: 44px;
    height: 22px;
    flex-shrink: 0;
  }

  .toggle-dot {
    width: 18px;
    height: 18px;
    left: 2px;
    top: 2px;
  }

  .ai-toggle.active .toggle-dot {
    transform: translateX(22px);
  }

  .toggle-label {
    font-size: 10px;
    right: 10px;
  }

  .ai-toggle.active .toggle-label {
    right: 26px;
  }

  .ai-input {
    height: 30px;
    font-size: 13px;
    flex: 1;
    min-width: 0;
  }

  .search-btn {
    width: 54px;
    height: 30px;
    padding: 0 10px;
    font-size: 13px;
    flex-shrink: 0;
  }

  .preview-panel {
    position: fixed;
    top: calc(var(--nav-height, 55px) + 10px);
    left: 50%;
    right: auto;
    transform: translateX(-50%) translateY(-8px);
    width: 90vw;
    max-width: 480px;
    z-index: 1000;
  }

  .preview-panel.visible {
    transform: translateX(-50%) translateY(0);
  }
}

@media (max-width: 460px) {
  .ai-search-wrap {
    padding: 3px;
    gap: 4px;
  }

  .ai-toggle {
    width: 40px;
    height: 20px;
    margin-left: 3px;
  }

  .toggle-dot {
    width: 16px;
    height: 16px;
  }

  .ai-toggle.active .toggle-dot {
    transform: translateX(20px);
  }

  .toggle-label {
    font-size: 9px;
    right: 8px;
  }

  .ai-toggle.active .toggle-label {
    right: 22px;
  }

  .ai-input {
    height: 28px;
    font-size: 12px;
  }

  .search-btn {
    width: 48px;
    height: 28px;
    padding: 0 10px;
    font-size: 12px;
    margin-right: 3px;
  }

  .preview-panel {
    max-width: 400px;
  }
}

@media (max-width: 400px) {
  .ai-search-wrap {
    padding: 3px;
    gap: 3px;
  }

  .ai-toggle {
    width: 36px;
    height: 18px;
  }

  .toggle-dot {
    width: 14px;
    height: 14px;
  }

  .ai-toggle.active .toggle-dot {
    transform: translateX(18px);
  }

  .toggle-label {
    font-size: 8px;
    right: 6px;
  }

  .ai-toggle.active .toggle-label {
    right: 20px;
  }

  .ai-input {
    height: 26px;
    font-size: 11px;
  }

  .search-btn {
    width: 40px;
    height: 26px;
    padding: 0 6px;
    font-size: 11px;
  }

  .preview-panel {
    max-width: 320px;
  }
}
</style>
